import { useState } from 'react';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { User, Check, ImagePlus, Upload } from 'lucide-react';
import type { IReferencePersonSelectorProps } from '@/@types/ai-studio';
import type { Id } from '../../../convex/_generated/dataModel';

interface IReferencePerson {
  _id: Id<'referencePersons'>;
  name: string;
  imageUrls: string[];
}

export function ReferencePersonSelector({
  selectedPersonId,
  onSelect,
  disabled = false,
  className,
}: IReferencePersonSelectorProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Fetch reference persons from Convex
  const referencePersons = useQuery(api.referencePersons.getActiveReferencePersons) as
    | IReferencePerson[]
    | undefined;
  const selectedPerson = referencePersons?.find(p => p._id === selectedPersonId);

  const handleSelectPerson = (personId: Id<'referencePersons'>) => {
    onSelect(personId);
    setIsDialogOpen(false);
  };

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {/* Two Selection Boxes Side by Side */}
      <div className="grid grid-cols-2 gap-2">
        {/* Left Box - Choose from existing reference persons */}
        <button
          type="button"
          disabled={disabled}
          onClick={() => setIsDialogOpen(true)}
          className={cn(
            'flex aspect-square items-center justify-center rounded-lg transition-all',
            'border-2 bg-muted/30',
            selectedPerson
              ? 'border-primary'
              : 'border-transparent hover:border-muted-foreground/50'
          )}
          title="Choose from existing reference persons"
        >
          {selectedPerson?.imageUrls?.[0] ? (
            <img
              src={selectedPerson.imageUrls[0]}
              alt={selectedPerson.name}
              className="size-full rounded-md object-cover"
            />
          ) : (
            <ImagePlus className="size-6 text-muted-foreground" />
          )}
        </button>

        {/* Right Box - Upload new reference images */}
        <label
          className={cn(
            'flex aspect-square items-center justify-center rounded-lg transition-all cursor-pointer',
            'border-2 bg-muted/30 border-transparent hover:border-muted-foreground/50',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
          title="Upload new reference images"
        >
          <Upload className="size-6 text-muted-foreground" />
          <input
            type="file"
            accept="image/*"
            multiple
            disabled={disabled}
            className="hidden"
            onChange={_e => {
              // TODO: Handle file upload
            }}
          />
        </label>
      </div>

      {/* Selection Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Select Reference Person</DialogTitle>
            <DialogDescription>
              Choose a reference person for generating images with consistent identity.
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="max-h-[400px]">
            <div className="flex flex-col gap-2 pr-4">
              {!referencePersons || referencePersons.length === 0 ? (
                <div className="flex flex-col items-center gap-4 py-8">
                  <div className="flex size-16 items-center justify-center rounded-full bg-muted">
                    <User className="size-8 text-muted-foreground" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-foreground">No reference persons</p>
                    <p className="text-xs text-muted-foreground">
                      Upload images using the upload button to create a reference person.
                    </p>
                  </div>
                </div>
              ) : (
                referencePersons.map(person => {
                  const isSelected = person._id === selectedPersonId;

                  return (
                    <button
                      key={person._id}
                      type="button"
                      onClick={() => handleSelectPerson(person._id)}
                      className={cn(
                        'flex items-center gap-3 rounded-lg border p-3 text-left transition-all',
                        isSelected
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-muted-foreground hover:bg-muted/50'
                      )}
                    >
                      {person.imageUrls?.[0] ? (
                        <img
                          src={person.imageUrls[0]}
                          alt={person.name}
                          className="size-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="flex size-12 items-center justify-center rounded-full bg-muted">
                          <User className="size-6 text-muted-foreground" />
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="text-sm font-medium text-foreground">{person.name}</p>
                      </div>
                      {isSelected && (
                        <div className="flex size-6 items-center justify-center rounded-full bg-primary">
                          <Check className="size-4 text-primary-foreground" />
                        </div>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
